/**
 * SearchCacheService
 *
 * Multi-layer caching service for command palette search results.
 * Provides instant lookups via in-memory cache with sessionStorage persistence.
 *
 * Features:
 * - L1: In-memory Map (sub-millisecond lookups)
 * - L2: SessionStorage (survives page refresh within session)
 * - TTL-based expiration per result type
 * - Request deduplication for concurrent identical queries
 * - Recent search history tracking
 */

/** Result types with different cache TTLs */
export type SearchResultType =
  | 'users'
  | 'cards'
  | 'card_accounts'
  | 'wallets'
  | 'instructions'
  | 'invoices'
  | 'tickets'
  | 'pages';

/** Cache entry structure */
interface CacheEntry<T> {
  data: T;
  timestamp: number;
  type: SearchResultType;
}

/** Configuration for cache behavior */
interface CacheConfig {
  /** TTL in milliseconds per result type */
  ttl: Record<SearchResultType, number>;
  /** Maximum entries in memory cache */
  maxMemoryEntries: number;
  /** SessionStorage key prefix */
  storagePrefix: string;
  /** Maximum recent searches to track */
  maxRecentSearches: number;
}

const DEFAULT_CONFIG: CacheConfig = {
  ttl: {
    users: 30_000,       // 30s - user data can change
    cards: 60_000,       // 60s - card status changes less frequently
    card_accounts: 60_000,
    wallets: 60_000,     // 60s - wallet metadata changes infrequently
    instructions: 30_000, // 30s - instruction status changes
    invoices: 30_000,    // 30s - invoice status changes
    tickets: 30_000,     // 30s - ticket status changes
    pages: Infinity,     // Static menu items never expire
  },
  maxMemoryEntries: 200,
  storagePrefix: 'search_cache_',
  maxRecentSearches: 10,
};

const RECENT_SEARCHES_KEY = 'command_palette_recent_searches';

/**
 * Singleton cache service for command palette search
 */
class SearchCacheService {
  private memoryCache: Map<string, CacheEntry<unknown>>;
  private inFlight: Map<string, Promise<unknown>>;
  private config: CacheConfig;

  constructor(config: Partial<CacheConfig> = {}) {
    this.memoryCache = new Map();
    this.inFlight = new Map();
    this.config = { ...DEFAULT_CONFIG, ...config };
    
    // Hydrate memory cache from sessionStorage on init
    this.hydrateFromStorage();
  }

  /**
   * Generate a cache key for a search query and result type
   */
  private getCacheKey(query: string, type: SearchResultType): string {
    return `${type}:${query.toLowerCase().trim()}`;
  }

  /**
   * Check if a cache entry is still valid based on TTL
   */
  private isValid(entry: CacheEntry<unknown>): boolean {
    const ttl = this.config.ttl[entry.type];
    if (ttl === Infinity) return true;
    return Date.now() - entry.timestamp < ttl;
  }

  /**
   * Get cached data for a query and type
   * @returns Cached data or null if not found/expired
   */
  get<T>(query: string, type: SearchResultType): T | null {
    const key = this.getCacheKey(query, type);
    
    // L1: Check memory cache first
    const memoryEntry = this.memoryCache.get(key);
    if (memoryEntry && this.isValid(memoryEntry)) {
      return memoryEntry.data as T;
    }
    
    // L2: Check sessionStorage
    const storageEntry = this.getFromStorage<T>(key);
    if (storageEntry && this.isValid(storageEntry)) {
      // Promote to memory cache
      this.memoryCache.set(key, storageEntry);
      return storageEntry.data;
    }
    
    // Cache miss - clean up expired entry if exists
    if (memoryEntry) {
      this.memoryCache.delete(key);
    }
    
    return null;
  }

  /**
   * Store data in cache
   */
  set<T>(query: string, type: SearchResultType, data: T): void {
    const key = this.getCacheKey(query, type);
    const entry: CacheEntry<T> = {
      data,
      timestamp: Date.now(),
      type,
    };
    
    // Enforce memory limit with LRU eviction
    if (this.memoryCache.size >= this.config.maxMemoryEntries) {
      const firstKey = this.memoryCache.keys().next().value;
      if (firstKey) {
        this.memoryCache.delete(firstKey);
      }
    }
    
    // L1: Store in memory
    this.memoryCache.set(key, entry);
    
    // L2: Persist to sessionStorage (skip for pages - they're always computed)
    if (type !== 'pages') {
      this.saveToStorage(key, entry);
    }
  }

  /**
   * Get cached data or fetch if not available
   * Handles request deduplication for concurrent calls
   */
  async getOrFetch<T>(
    query: string,
    type: SearchResultType,
    fetcher: () => Promise<T>,
    signal?: AbortSignal
  ): Promise<{ data: T; fromCache: boolean }> {
    // Check cache first
    const cached = this.get<T>(query, type);
    if (cached !== null) {
      return { data: cached, fromCache: true };
    }
    
    const key = this.getCacheKey(query, type);
    
    // Check if there's already an in-flight request for this key
    const existing = this.inFlight.get(key);
    if (existing) {
      const data = await existing as T;
      return { data, fromCache: false };
    }
    
    // Create new request with abort handling
    const fetchPromise = (async () => {
      try {
        // Check if already aborted
        if (signal?.aborted) {
          throw new DOMException('Aborted', 'AbortError');
        }
        
        const data = await fetcher();
        
        // Only cache if not aborted
        if (!signal?.aborted) {
          this.set(query, type, data);
        }
        
        return data;
      } finally {
        this.inFlight.delete(key);
      }
    })();
    
    this.inFlight.set(key, fetchPromise);
    
    const data = await fetchPromise;
    return { data, fromCache: false };
  }

  /**
   * Invalidate cache entries matching a pattern
   * @param pattern - Optional prefix to match (e.g., 'users:' to clear all user caches)
   */
  invalidate(pattern?: string): void {
    if (!pattern) {
      // Clear everything
      this.memoryCache.clear();
      this.clearStorage();
      return;
    }
    
    // Clear matching entries
    for (const key of this.memoryCache.keys()) {
      if (key.startsWith(pattern)) {
        this.memoryCache.delete(key);
        this.removeFromStorage(key);
      }
    }
  }

  /**
   * Prefetch results for a list of queries
   * Used to pre-warm cache when modal opens
   */
  async prefetch(
    queries: string[],
    fetchers: Record<SearchResultType, (query: string) => Promise<unknown>>
  ): Promise<void> {
    const types: SearchResultType[] = ['users', 'instructions', 'cards'];
    
    const prefetchPromises: Promise<void>[] = [];
    
    for (const query of queries) {
      for (const type of types) {
        // Skip if already cached
        if (this.get(query, type) !== null) continue;
        
        const fetcher = fetchers[type];
        if (fetcher) {
          prefetchPromises.push(
            this.getOrFetch(query, type, () => fetcher(query))
              .then(() => {})
              .catch(() => {}) // Silently ignore prefetch errors
          );
        }
      }
    }
    
    // Don't await - prefetch in background
    Promise.all(prefetchPromises);
  }

  // ============================================================================
  // Recent Searches
  // ============================================================================

  /**
   * Get recent search queries
   */
  getRecentSearches(): string[] {
    try {
      const stored = localStorage.getItem(RECENT_SEARCHES_KEY);
      if (!stored) return [];
      return JSON.parse(stored) as string[];
    } catch {
      return [];
    }
  }

  /**
   * Add a search query to recent history
   */
  addRecentSearch(query: string): void {
    const trimmed = query.trim().toLowerCase();
    if (trimmed.length < 2) return;
    
    const recent = this.getRecentSearches();
    
    // Remove if already exists (will be re-added at front)
    const filtered = recent.filter(q => q !== trimmed);
    
    // Add to front
    filtered.unshift(trimmed);
    
    // Limit size
    const limited = filtered.slice(0, this.config.maxRecentSearches);
    
    try {
      localStorage.setItem(RECENT_SEARCHES_KEY, JSON.stringify(limited));
    } catch {
      // localStorage full or unavailable - ignore
    }
  }

  /**
   * Clear recent search history
   */
  clearRecentSearches(): void {
    localStorage.removeItem(RECENT_SEARCHES_KEY);
  }

  // ============================================================================
  // SessionStorage Helpers
  // ============================================================================

  private getStorageKey(cacheKey: string): string {
    return `${this.config.storagePrefix}${cacheKey}`;
  }

  private getFromStorage<T>(key: string): CacheEntry<T> | null {
    try {
      const stored = sessionStorage.getItem(this.getStorageKey(key));
      if (!stored) return null;
      return JSON.parse(stored) as CacheEntry<T>;
    } catch {
      return null;
    }
  }

  private saveToStorage<T>(key: string, entry: CacheEntry<T>): void {
    try {
      sessionStorage.setItem(this.getStorageKey(key), JSON.stringify(entry));
    } catch {
      // Storage full or unavailable - ignore
    }
  }

  private removeFromStorage(key: string): void {
    try {
      sessionStorage.removeItem(this.getStorageKey(key));
    } catch {
      // Ignore
    }
  }

  private clearStorage(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key?.startsWith(this.config.storagePrefix)) {
          keysToRemove.push(key);
        }
      }
      for (const key of keysToRemove) {
        sessionStorage.removeItem(key);
      }
    } catch {
      // Ignore
    }
  }

  private hydrateFromStorage(): void {
    try {
      for (let i = 0; i < sessionStorage.length; i++) {
        const storageKey = sessionStorage.key(i);
        if (!storageKey?.startsWith(this.config.storagePrefix)) continue;
        
        const cacheKey = storageKey.slice(this.config.storagePrefix.length);
        const entry = this.getFromStorage(cacheKey);
        
        if (entry && this.isValid(entry)) {
          this.memoryCache.set(cacheKey, entry);
        } else {
          // Clean up expired entry
          sessionStorage.removeItem(storageKey);
        }
      }
    } catch {
      // Ignore hydration errors
    }
  }
}

// Export singleton instance
export const searchCache = new SearchCacheService();

// Export class for testing or custom instances
export { SearchCacheService };
